import { useEffect } from 'react';
import { createPortal } from 'react-dom';
import RiskCaseCard from './RiskCaseCard';

/**
 * TABLE DETAIL CANON — ℹ opens the same case card as the cards grid.
 *
 * Contract: `./riskCardLayoutCanon.js`.
 * MUST render RiskCaseCard (tabs + RiskHitPanelLayout). Do not invent a
 * second detail layout for the table. Size shell: max-w-xl around the card.
 */
export default function RiskCaseDetailModal({
  riskCase,
  organizationLabel,
  onClose,
  onOpenClientLk,
  openingUserId,
  onOpenHistory,
  commentsOpen = false,
  onToggleComments,
  /** Prefer this risk tab when opening from a multi-risk table row. */
  initialRiskNumber = null,
}) {
  useEffect(() => {
    if (!riskCase) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (event) => {
      if (event.key === 'Escape') onClose?.();
    };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener('keydown', onKey);
    };
  }, [riskCase, onClose]);

  if (!riskCase || typeof document === 'undefined') {
    return null;
  }

  const preferredRisk =
    initialRiskNumber != null && Number.isFinite(Number(initialRiskNumber))
      ? Number(initialRiskNumber)
      : riskCase.summaryRiskNumber != null && Number.isFinite(Number(riskCase.summaryRiskNumber))
        ? Number(riskCase.summaryRiskNumber)
        : null;

  return createPortal(
    <div
      className="fixed inset-0 z-50 flex items-stretch justify-center overflow-y-auto bg-slate-950/60 sm:items-start sm:p-6"
      role="dialog"
      aria-modal="true"
      aria-label="Карточка риска"
      data-risk-case-detail-modal="canon"
    >
      <button
        type="button"
        aria-label="Закрыть"
        className="fixed inset-0 cursor-default"
        onClick={onClose}
      />
      <div className="relative z-10 flex min-h-full w-full max-w-xl flex-col sm:min-h-0 sm:py-2">
        <div className="sticky top-0 z-20 mb-2 flex items-center justify-between gap-2 px-3 pt-[max(0.5rem,env(safe-area-inset-top))] sm:mb-3 sm:justify-end sm:px-0 sm:pt-0">
          <p className="truncate text-sm font-medium text-white sm:hidden">Карточка риска</p>
          <button
            type="button"
            onClick={onClose}
            className="min-h-10 rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-700 shadow-sm dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 sm:min-h-0 sm:py-1.5"
          >
            Закрыть
          </button>
        </div>

        <div className="mx-auto w-full flex-1 px-3 pb-24 sm:px-0 sm:pb-0">
          <RiskCaseCard
            riskCase={riskCase}
            organizationLabel={organizationLabel}
            onOpenClientLk={onOpenClientLk}
            openingUserId={openingUserId}
            onOpenHistory={onOpenHistory}
            commentsOpen={commentsOpen}
            onToggleComments={onToggleComments}
            initialRiskNumber={preferredRisk}
          />
        </div>

        <div className="sticky bottom-0 z-20 border-t border-slate-200 bg-white/95 p-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] backdrop-blur dark:border-slate-800 dark:bg-slate-950/95 sm:hidden">
          <button
            type="button"
            onClick={onClose}
            className="min-h-11 w-full rounded-xl border border-slate-300 bg-white px-3 py-2.5 text-sm font-medium text-slate-800 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100"
          >
            Закрыть
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
